import type { CurrentUser } from '@/shared/api/generated/tenancy';

/* May this caller do this thing?
 *
 * **The string order is `action:resource_type`, and it is the one thing to get right here.**
 * `authz.role.permissions` stores it that way and `sqlalchemy_directory._permission` parses it that
 * way; `Permission.key` in the kernel renders the *reverse* for logs, and its own docstring warns
 * that writing roles from that property produces permissions which parse back swapped, match
 * nothing, and grant silently nothing. The API sends the stored form - confirmed on the wire - so
 * every call site here spells it `read:identity.credential`, never `identity.credential:read`.
 *
 * **This hides controls. It does not protect anything.** Every route refuses server-side regardless
 * of what the browser believes, which is the property the backend's authorization exists to hold.
 * `can` is here so a user is not offered a door that will slam, and for no other reason.
 */

export function can(identity: CurrentUser | undefined, permission: string): boolean {
  /* Undefined is "we have not read it yet", and it denies. Optimistically showing a control while
   * the identity loads means the control appears and then vanishes, which reads as a bug; and if
   * the read fails it would never vanish at all. */
  if (identity === undefined) return false;
  return identity.permissions.includes(permission);
}

/* The permissions the Admin surface is gated on.
 *
 * **`grant:authz.role`, deliberately not the read.** `READER` is derived as one `read` per resource
 * type, so *every* reader holds `read:identity.credential` and `read:authz.role` - gating the Admin
 * group on a read would put it in front of every analyst in the tenant, who would then be refused
 * the moment they touched a control. The write is the honest gate: only `admin` holds it.
 */
export const ADMIN_PERMISSION = 'grant:authz.role';
export const READ_USERS_PERMISSION = 'read:identity.credential';
export const DISABLE_USER_PERMISSION = 'disable:identity.credential';
/* **`delete`, and only `admin` holds it — deliberately not folded into `disable`.** The two are
 * different decisions: a disabled account can be enabled again, and a deleted one reserves its email
 * address permanently, so a deployment that wants an operations role able to offboard somebody
 * without being able to burn an address has somewhere to draw that line. Spelled
 * `action:resource_type` like every other entry here — see the note at the top of this file, because
 * the reverse order parses back swapped and grants silently nothing. */
export const DELETE_USER_PERMISSION = 'delete:identity.credential';
export const READ_ROLES_PERMISSION = 'read:authz.role';

/* The three actions on a source that reach `admin` only, and why each is its own action rather than
 * an `update` — `catalog.connection` deliberately has none, and `permissions.py` carries the argument
 * in full.
 *
 * **`disable` covers both directions.** Whoever may take a source out of service may put it back;
 * splitting them would permit a grant that can silence a source and cannot undo it, which is the trap
 * the forced-password-change flow avoids one module over. So one control, one permission, and the
 * button renders whichever direction applies.
 *
 * **`rotate_credential` decides which account this platform authenticates to a customer's production
 * database as**, which is not the same privilege as registering sources — an onboarding operator adds
 * connections all day and is not thereby trusted to replace the credential behind an existing one.
 *
 * All three spelled `action:resource_type`, like every entry above. See the note at the top of this
 * file: the reverse order parses back swapped and grants silently nothing.
 */
export const DISABLE_SOURCE_PERMISSION = 'disable:catalog.connection';
export const ROTATE_CREDENTIAL_PERMISSION = 'rotate_credential:catalog.connection';
export const DELETE_SOURCE_PERMISSION = 'delete:catalog.connection';

/* **`publish` is not held by `data-steward`, which is the surprising one and the reason this is
 * here.** `STEWARD` has `create`, `update` and `submit` on `rules.rule`; `publish` sits with
 * `APPROVER` and `ADMIN`, because freezing an expression a binding will point at is a governance act
 * rather than an editing one. So the role that authors rules cannot publish their first version, and
 * a form that assumes otherwise creates a rule and then 403s. */
export const PUBLISH_RULE_PERMISSION = 'publish:rules.rule';

/* **The signature on a two-eyes rule activation.** `APPROVER` and `ADMIN` hold it; `READER` and
 * `STEWARD` do not — see `PUBLISH_RULE_PERMISSION` above for why the author/approve split puts it
 * there.
 *
 * It is the gate on *offering* a signature, and it is deliberately not the whole rule. `ApprovalService`
 * refuses the requester's own signature by **identity**, so holding this permission does not mean a
 * given request is signable by this caller — see `approvalIsSignable` in `features/notifications`,
 * which pairs the permission with the authorship check. Treating the permission as sufficient is how a
 * screen comes to offer an admin a control that answers 403 on their own request. */
export const ACTIVATE_RULE_PERMISSION = 'activate:rules.rule';

/* The three reads a notification feed needs before it may ask a question at all.
 *
 * **A source the caller is not permitted to read is a known nothing, not a blind spot**, and that
 * distinction is why these exist as constants rather than being left to a 403. Issuing the request
 * anyway would make an ordinary permission boundary arrive as a failed read, which a feed then has to
 * report as "we could not find out" — the one sentence that must be reserved for a genuine fault.
 * **This is not a hypothetical case.** `STEWARD`'s reads were hand-listed for most of this
 * platform's life while `READER`'s are derived, so the working role could read fourteen fewer
 * resource types than the lowest-privilege one — `observability.incident` among them. That is being
 * corrected on the backend (`STEWARD` now inherits `READER`'s reads), and the gate stays either way:
 * a hand-written role in a customer's tenant can withhold any of these, and a header that discovered
 * that boundary as a red banner would be reporting a deployment's own policy as a fault. */
export const READ_RULES_PERMISSION = 'read:rules.rule';

/* Editing a rule binding — enabling or disabling one. What the bulk starter needs to be offered:
 * the same permission `RuleService.bind`/`disable_binding` and the bulk-run service all authorize
 * as `update:rules.binding`. A reader sees the run history and not the starter; the history is not
 * the secret, the ability to change what runs is. */
export const EDIT_BINDINGS_PERMISSION = 'update:rules.binding';
export const READ_INCIDENTS_PERMISSION = 'read:observability.incident';
export const READ_CLASSIFICATIONS_PERMISSION = 'read:catalog.classification';

/* Reading remediation plans. The bell needs it to answer "is a plan waiting on me" — and it is a
 * *read*, deliberately, not the approve permission: a reader who may see plans and not sign them
 * still learns nothing they should not, and gating the feed on `approve` would make the row vanish
 * for anybody whose grant arrives later than their curiosity. Whether they may actually sign is the
 * server's `decision_refusal`, which is a stricter question than any permission this file names. */
export const READ_PLANS_PERMISSION = 'read:remediation.plan';

/* Computing a score from a finished run. `data-steward` and `admin` hold it; `reader` does not —
 * which is right, since a score is a published judgement about a dataset rather than a read of one. */
export const COMPUTE_SCORE_PERMISSION = 'compute:scoring.score';

/* The two compliance acts, and they are held by the *same* pair of roles for different reasons.
 *
 * **`register:compliance.framework` and `generate:compliance.pack` both reach `approver` and `admin`
 * only.** `reader` and `data-steward` hold the two reads and neither write — which is right and is
 * the surprising half, because a steward is the role that works this screen all day. Registering a
 * framework is a governance act: a version is immutable once registered, packs embed it, and a
 * different definition under an existing version is refused with a 409. Generating a pack is the
 * act that produces evidence somebody hands an auditor.
 *
 * So a steward opening `/compliance` must not be offered either control. Without these constants
 * they were: the buttons rendered and answered 403, which is the door-that-slams shape `can`'s own
 * docstring exists to prevent.
 */
export const REGISTER_FRAMEWORK_PERMISSION = 'register:compliance.framework';
export const GENERATE_PACK_PERMISSION = 'generate:compliance.pack';

/* Reading the tamper-evident chain itself — the entries, not a verification result.
 *
 * **`approver` does not hold it, and that is the point of naming it here.** `READER` derives one
 * `read` per governed resource type and `STEWARD` inherits those, so both can read the chain;
 * `APPROVER`'s grants are hand-curated and `audit.chain` is not among them. So the person who signs
 * and retires a contract is exactly the person who cannot read its history.
 *
 * That is a deliberate shape rather than a gap — signing and retiring do not require reading the
 * chain — and a per-record history panel has to degrade for them rather than fire a request it knows
 * will 403. Asking anyway would put a refusal in the tenant's own audit-adjacent logs for a fact the
 * caller's token already carries, and would make "you may not see this" arrive as a failure.
 *
 * Spelled `action:resource_type` like every entry above; the reverse parses back swapped and grants
 * silently nothing.
 */
export const READ_AUDIT_CHAIN_PERMISSION = 'read:audit.chain';

/* The contract lifecycle, and it splits across two roles rather than sitting with one.
 *
 * **`data-steward` submits and evaluates; `approver` signs and retires.** Neither can do the other's
 * half, which is the author/approve separation `ApprovalPolicy(eyes=2)` exists to hold: the role that
 * writes a promise is not the role that signs it. `reader` holds the read alone.
 *
 * A permission is the gate on *offering* a control and is deliberately not the whole rule. The server
 * refuses a signature from anyone but the declared subject for that side with a 403 — even with every
 * permission — so holding `sign:contracts.contract` does not mean a given contract is signable by
 * this caller. Treating the permission as sufficient is how a screen comes to offer an approver a
 * button that answers 403 on somebody else's contract.
 */
export const CREATE_CONTRACT_PERMISSION = 'create:contracts.contract';
export const SUBMIT_CONTRACT_PERMISSION = 'submit:contracts.contract';
export const SIGN_CONTRACT_PERMISSION = 'sign:contracts.contract';
export const ACTIVATE_CONTRACT_PERMISSION = 'activate:contracts.contract';
export const EVALUATE_CONTRACT_PERMISSION = 'evaluate:contracts.contract';
export const RETIRE_CONTRACT_PERMISSION = 'retire:contracts.contract';

/** Whether this caller should be offered the Admin section at all. */
export function isAdmin(identity: CurrentUser | undefined): boolean {
  return can(identity, ADMIN_PERMISSION);
}

/* The Marketplace, and its six actions are the plugin lifecycle's separation of duties.
 *
 * **`submit`, `review` and `publish` are held by different people on purpose.** A steward submits
 * and cannot review; an approver reviews and publishes and cannot submit. The server enforces both
 * halves independently of these constants — a submitter reviewing their own plugin is refused 409
 * even holding `review:marketplace.plugin`, and a requester approving their own publication is
 * refused 403 — so holding a permission here never means a *particular* plugin is actionable by
 * this caller. Gating on the permission alone is how a screen offers a button that answers 403.
 *
 * `install` and `run` are the tenant's side and belong to the steward: installing grants a
 * capability subset, running spends nothing but reads metadata the caller must separately be
 * permitted to see. `read` is every reader's, derived per resource type.
 *
 * Spelled `action:resource_type` like every entry above — the reverse parses back swapped and
 * grants silently nothing.
 */
export const READ_PLUGINS_PERMISSION = 'read:marketplace.plugin';
export const SUBMIT_PLUGIN_PERMISSION = 'submit:marketplace.plugin';
export const REVIEW_PLUGIN_PERMISSION = 'review:marketplace.plugin';
export const PUBLISH_PLUGIN_PERMISSION = 'publish:marketplace.plugin';
export const INSTALL_PLUGIN_PERMISSION = 'install:marketplace.plugin';
export const RUN_PLUGIN_PERMISSION = 'run:marketplace.plugin';

/* The reconciliation surface, which is gated in four places and read in a fifth.
 *
 * **`data-steward` and `admin` hold all of these; `reader` holds only the reads; `approver` holds
 * nothing at all on this module.** That last one is not an oversight and must not be designed
 * around: every read the approver role holds is paired with an action it takes, and it takes no
 * reconciliation action — so the tab reports that this surface belongs to stewards rather than
 * rendering an error. Checked against the role definitions in `lumiq.app.permissions`.
 *
 * Spelled `action:resource_type` like every entry above — the reverse order parses back swapped and
 * grants silently nothing.
 */
export const READ_RECONCILIATION_PERMISSION = 'read:reconciliation.recon';
export const CREATE_RECONCILIATION_PERMISSION = 'create:reconciliation.recon';
export const RUN_RECONCILIATION_PERMISSION = 'run:reconciliation.recon';
/* **`publish` is the tolerance version, and it is its own action for the reason the version is
 * immutable**: a published bound is permanent history that every past run keeps citing, which is a
 * governance act rather than an editing one. */
export const PUBLISH_RECONCILIATION_PERMISSION = 'publish:reconciliation.recon';
export const CONFIGURE_RECONCILIATION_PERMISSION = 'configure:reconciliation.recon';
/* **Its own resource type, deliberately not an action on `reconciliation.recon`.** "Reconcile now"
 * is bounded by a person doing it and watching; this is "join two of these tables every night for
 * ever with nobody watching", and a deployment must be able to grant the first and withhold the
 * second. */
export const CONFIGURE_RUN_SCHEDULE_PERMISSION = 'configure:reconciliation.run_schedule';
